import { useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import StatCard from '../../../components/StatCard.jsx';
import { backtestApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import ErrorNote from '../ErrorNote.jsx';
import useRunResult from '../../../utils/useRunResult.js';
import { engineConfig } from '../../../utils/engineConfig.js';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

/**
 * Monte-Carlo mode: wraps the base BacktestRequest with a resample method +
 * iteration count. Renders the outcome distribution (p5/p50/p95) + a
 * dependency-free bar histogram, per OBT_INTEGRATION_SPEC.md §5.
 */
export default function MonteCarloMode({ config, onConfigChange, wallet, onRan, lastRun, onSaveRun, rulesStrategy }) {
  const [method, setMethod] = useState('resample');
  const [iterations, setIterations] = useState(500);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useRunResult(lastRun?.result, true);
  const [error, setError] = useState(null);

  const request = {
    base: {
      mode: 'rules',
      strategy: rulesStrategy,
      symbols: config.symbols,
      period: config.period,
      config: engineConfig(config),
    },
    method,
    iterations,
  };

  async function run() {
    setError(null);
    setRunning(true);
    try {
      const data = await backtestApi.montecarlo(request);
      setResult(data);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  }

  const distribution = result?.distribution || result?.return_pct || null;
  const histogram = result?.histogram || [];
  const maxCount = histogram.length ? Math.max(...histogram.map((b) => b.count), 1) : 1;

  return (
    <div className="aq-grid aq-grid--2">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ConfigPanel config={config} onChange={onConfigChange} wallet={wallet} />

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Monte-Carlo simulation
          </h3>
          <div className="aq-bt-params">
            <label className="aq-field">
              <span>Method</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="resample">Resample realized trade P&amp;L</option>
                <option value="shuffle">Shuffle realized trade P&amp;L</option>
              </select>
            </label>
            <label className="aq-field">
              <span>Iterations</span>
              <input type="number" value={iterations} onChange={(e) => setIterations(Number(e.target.value))} />
            </label>
          </div>

          <StrategyPreview title="montecarlo request preview" value={request} />

          <div style={{ marginTop: 14 }}>
            <Button disabled={running || !rulesStrategy || !isBacktestPeriodValid(config.period)} onClick={run}>
              {running ? 'Simulating…' : 'Run Monte-Carlo'}
            </Button>
            {!rulesStrategy && <p className="aq-note">Build a strategy in Rules Builder before simulating it.</p>}
          </div>
        </Card>
      </div>

      <Card>
        <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
          Outcome distribution
        </h3>
        <ErrorNote error={error} />
        {!result && !error && <p className="aq-module-desc">Run a simulation to see the outcome distribution.</p>}
        {result && (
          <>
            <div className="aq-grid aq-grid--4">
              <StatCard label="P5" value={`${Number(distribution?.p5 ?? 0).toFixed(2)}%`} delta={distribution?.p5} />
              <StatCard label="P50 (median)" value={`${Number(distribution?.p50 ?? 0).toFixed(2)}%`} delta={distribution?.p50} />
              <StatCard label="P95" value={`${Number(distribution?.p95 ?? 0).toFixed(2)}%`} delta={distribution?.p95} />
              <StatCard label="Mean" value={`${Number(distribution?.mean ?? 0).toFixed(2)}%`} delta={distribution?.mean} />
            </div>

            {histogram.length > 0 && <><h4 className="heading" style={{ fontSize: 13, margin: '16px 0 8px' }}>
              Return histogram ({result.iterations} iterations)
            </h4><div className="aq-hist">
              {histogram.map((b, i) => (
                <div
                  key={i}
                  className="aq-hist-bar"
                  style={{ height: `${Math.max(2, (b.count / maxCount) * 100)}%` }}
                  title={`${b.bucketStart}% to ${b.bucketEnd}%: ${b.count}`}
                />
              ))}
            </div><p className="aq-module-desc">
              Range {histogram[0]?.bucketStart}% to {histogram[histogram.length - 1]?.bucketEnd}% total
              return.
            </p></>}
            {result.prob_profit != null && <p className="aq-module-desc">Probability of profit: {(Number(result.prob_profit) * 100).toFixed(2)}% · method {result.method}</p>}
          </>
        )}
      </Card>
    </div>
  );
}
